import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    Select,
    Switch,
    Card,
    Row,
    Col,
    Spin,
    Space,
    Divider,
    Typography,
    message
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import * as adminactions from 'store/actions/AuthActions';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const LinkForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [linkSlug, setLinkSlug] = useState('');

    const linkTypes = [
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
        { value: 'documentation', label: 'Documentation' },
        { value: 'api', label: 'API Endpoint' },
        { value: 'resource', label: 'Resource' },
        { value: 'tool', label: 'Tool' },
    ];

    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'deprecated', label: 'Deprecated' },
        { value: 'archived', label: 'Archived' },
    ];

    useEffect(() => {
        loadReferenceData();
        if (id) {
            loadLinkData();
        }
    }, [id]);

    const loadReferenceData = async () => {
        const categoriesData = await actions.loadAllLinkCategories();
        setCategories(categoriesData || []);

        const tagsData = await actions.loadLinkTags();
        setTags(tagsData || []);

        const empRes = await adminactions.loadAllEmployees();
        if (empRes?.data?.results) setEmployees(empRes.data.results);

        const rolesData = await actions.loadRoles();
        setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.results || []);
    };

    const loadLinkData = async () => {
        setLoading(true);
        try {
            const data = await actions.fetchLink(id);
            if (data && !data.response) {
                setLinkSlug(data.slug || '');
                form.setFieldsValue({
                    ...data,
                    category_id: data.category,
                    tags: data.tags || [],
                    allowed_employee_ids: data.allowed_employees || [],
                });
            }
        } catch (err) {
            console.error('Error loading link:', err);
        }
        setLoading(false);
    };

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                ...values,
                category_id: values.category_id || null,
                tags: values.tags || [],
                allowed_roles: values.allowed_roles || ['ADMIN'],
                allowed_employee_ids: values.allowed_employee_ids || [],
            };

            if (id) {
                await actions.updateLink(payload, id);
            } else {
                await actions.addLink(payload);
            }
            navigate('/links');
        } catch (err) {
            console.error('Error saving link:', err);
        }
        setSubmitting(false);
    };



    const getRedirectUrl = () => {
        return `${window.location.origin}/l/${linkSlug}/`;
    };

    const copyRedirectUrl = () => {
        navigator.clipboard.writeText(getRedirectUrl());
        message.success('Redirect URL copied to clipboard');
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <Card
                title={
                    <Space>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/links')}
                        />
                        {id ? 'Edit Link' : 'Create New Link'}
                    </Space>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        link_type: 'external',
                        status: 'draft',
                        is_public: false,
                        is_pinned: false,
                        current_version: '1.0.0',
                        tags: [],
                        allowed_roles: ['ADMIN'],
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Title"
                                name="title"
                                rules={[{ required: true, message: 'Please enter a title' }]}
                            >
                                <Input
                                    placeholder="Enter link title"
                                    onChange={(e) => {
                                        if (!id) {
                                            form.setFieldsValue({ slug: generateSlug(e.target.value) });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Slug"
                                name="slug"
                                rules={[{ required: true, message: 'Please enter a slug' }]}
                            >
                                <Input placeholder="link-slug" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="URL"
                        name="url"
                        rules={[
                            { required: true, message: 'Please enter a URL' },
                            { type: 'url', message: 'Please enter a valid URL' }
                        ]}
                    >
                        <Input placeholder="https://example.com" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Category"
                                name="category_id"
                            >
                                <Select
                                    placeholder="Select a category"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map(cat => (
                                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Link Type"
                                name="link_type"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    {linkTypes.map(type => (
                                        <Option key={type.value} value={type.value}>{type.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Status"
                                name="status"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    {statusOptions.map(status => (
                                        <Option key={status.value} value={status.value}>{status.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Short Description"
                        name="short_description"
                    >
                        <Input placeholder="Brief description (max 500 characters)" maxLength={500} />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <TextArea rows={4} placeholder="Detailed description" />
                    </Form.Item>

                    <Divider>Versioning</Divider>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label="Version"
                                name="current_version"
                            >
                                <Input placeholder="1.0.0" />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item
                                label="Version Notes"
                                name="version_notes"
                            >
                                <Input placeholder="Notes for this version" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Tags</Divider>

                    <Form.Item
                        label="Tags"
                        name="tags"
                    >
                        <Select
                            mode="tags"
                            placeholder="Add tags"
                            style={{ width: '100%' }}
                        >
                            {tags.map(tag => (
                                <Option key={tag.name} value={tag.name}>{tag.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider>Access Control</Divider>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item
                                label="Public"
                                name="is_public"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Pinned"
                                name="is_pinned"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Allowed Roles"
                                name="allowed_roles"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select roles"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={roles.map(role => ({
                                        label: role.role_name,
                                        value: role.role_name,
                                    }))}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Allowed Employees"
                        name="allowed_employee_ids"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select employees who can access this link"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={employees.map(emp => ({
                                label: `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.employee_id,
                                value: emp.id,
                            }))}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    {id && linkSlug && (
                        <Form.Item label="Redirect URL">
                            <Space>
                                <Text copyable={false} code>{getRedirectUrl()}</Text>
                                <Button
                                    icon={<CopyOutlined />}
                                    size="small"
                                    onClick={copyRedirectUrl}
                                >
                                    Copy
                                </Button>
                            </Space>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                            >
                                {id ? 'Update Link' : 'Create Link'}
                            </Button>
                            <Button onClick={() => navigate('/links')}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default LinkForm;
